import React, { useState, useRef, useEffect, useCallback } from "react";
import Logo from "../assets/logo.png";
import LogoLeft from "../assets/LoginLeft.png";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import { forgotPasswordOTP, verifyOTP, resetPassword } from "../apis/api";

const ForgotPassword = () => {
  const toast = useRef(null);
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [lookupField, setLookupField] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sentEmails, setSentEmails] = useState([]);

  const [otpExpiry, setOtpExpiry] = useState(0);
  const [otpExpired, setOtpExpired] = useState(false);
  const timerRef = useRef(null);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const startTimer = useCallback((duration) => {
    setOtpExpiry(duration);
    setOtpExpired(false);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setOtpExpiry((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setOtpExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const showToast = (severity, summary, detail) => {
    toast.current.show({ severity, summary, detail, life: 3000 });
  };

  const handleSendOTP = async () => {
    if (!identifier.trim()) {
      showToast("error", "Error", "Please enter your Membership ID or Email");
      return;
    }
    setLoading(true);
    try {
      const res = await forgotPasswordOTP(identifier.trim());
      setSentEmails(res.email || []);
      setLookupField(res.lookupField || identifier.trim());
      setOtp("");
      showToast("success", "OTP Sent", res.message);
      setStep(2);
      startTimer(res.expiresIn || 300);
    } catch (err) {
      const msg =
        err.response?.data?.message || "Failed to send OTP. Please try again.";
      showToast("error", "Error", msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim()) {
      showToast("error", "Error", "Please enter the OTP");
      return;
    }
    if (otpExpired) {
      showToast("error", "OTP Expired", "OTP has expired. Please request a new one.");
      return;
    }
    setLoading(true);
    try {
      await verifyOTP(lookupField, otp.trim());
      if (timerRef.current) clearInterval(timerRef.current);
      showToast("success", "Verified", "OTP verified successfully");
      setStep(3);
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.message || "Invalid OTP. Please try again.";
      if (data?.expired) {
        setOtpExpired(true);
        if (timerRef.current) clearInterval(timerRef.current);
        showToast("error", "OTP Expired", msg);
      } else {
        showToast("error", "Invalid OTP", msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOtp("");
    setOtpExpired(false);
    await handleSendOTP();
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      showToast("error", "Error", "Please enter a new password");
      return;
    }
    if (newPassword.length < 6) {
      showToast("error", "Error", "Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast("error", "Error", "Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await resetPassword(lookupField, newPassword);
      showToast("success", "Success", "Password reset successful. You can now login.");
      setTimeout(() => navigate("/"), 2000);
    } catch (err) {
      const msg =
        err.response?.data?.message || "Failed to reset password. Please try again.";
      showToast("error", "Error", msg);
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <>
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <i
          style={{ fontSize: "30px", margin: "10px" }}
          className="pi pi-envelope"
        ></i>
        <div style={{ fontSize: "22px", fontWeight: "700" }}>
          Forgot Password
        </div>
        <div style={{ color: "#666", marginTop: "8px", fontSize: "14px" }}>
          Enter your Membership ID or Email to receive an OTP
        </div>
      </div>

      <div className="p-field" style={{ marginBottom: "20px" }}>
        <label style={{ display: "block", marginBottom: "6px", fontWeight: "600" }}>
          Membership ID or Email
        </label>
        <InputText
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          placeholder="Enter Membership ID or Email"
          style={{ height: "45px", width: "100%" }}
          onKeyDown={(e) => e.key === "Enter" && handleSendOTP()}
        />
      </div>

      <Button
        label={loading ? "Sending OTP..." : "Send OTP"}
        onClick={handleSendOTP}
        disabled={loading}
        style={{ background: "#2A2B8F", height: "45px", width: "100%" }}
      />
    </>
  );

  const renderStep2 = () => (
    <>
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <i
          style={{ fontSize: "30px", margin: "10px" }}
          className="pi pi-key"
        ></i>
        <div style={{ fontSize: "22px", fontWeight: "700" }}>Verify OTP</div>
        <div style={{ color: "#666", marginTop: "8px", fontSize: "14px" }}>
          Enter the OTP sent to your registered email
        </div>
        {sentEmails.length > 0 && (
          <div style={{ color: "#2A2B8F", marginTop: "4px", fontSize: "13px" }}>
            Sent to: {sentEmails.join(", ")}
          </div>
        )}
      </div>

      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        {otpExpired ? (
          <div style={{ color: "#e74c3c", fontWeight: "600", fontSize: "14px" }}>
            OTP has expired
          </div>
        ) : (
          <div style={{ color: "#2A2B8F", fontWeight: "600", fontSize: "16px" }}>
            Time remaining: {formatTime(otpExpiry)}
          </div>
        )}
      </div>

      <div className="p-field" style={{ marginBottom: "20px" }}>
        <label style={{ display: "block", marginBottom: "6px", fontWeight: "600" }}>
          OTP Code
        </label>
        <InputText
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          placeholder="Enter 5-digit OTP"
          style={{ height: "45px", width: "100%" }}
          maxLength={5}
          disabled={otpExpired}
          onKeyDown={(e) => e.key === "Enter" && !otpExpired && handleVerifyOTP()}
        />
      </div>

      {otpExpired ? (
        <Button
          label={loading ? "Resending..." : "Resend OTP"}
          onClick={handleResendOTP}
          disabled={loading}
          style={{ background: "#2A2B8F", height: "45px", width: "100%" }}
        />
      ) : (
        <Button
          label={loading ? "Verifying..." : "Verify OTP"}
          onClick={handleVerifyOTP}
          disabled={loading}
          style={{ background: "#2A2B8F", height: "45px", width: "100%" }}
        />
      )}

      <div style={{ textAlign: "center", marginTop: "12px" }}>
        <span
          style={{ color: "#2A2B8F", cursor: "pointer", fontSize: "14px" }}
          onClick={() => {
            if (timerRef.current) clearInterval(timerRef.current);
            setStep(1);
          }}
        >
          Change Membership ID or Email
        </span>
      </div>
    </>
  );

  const renderStep3 = () => (
    <>
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <i
          style={{ fontSize: "30px", margin: "10px" }}
          className="pi pi-lock"
        ></i>
        <div style={{ fontSize: "22px", fontWeight: "700" }}>
          Reset Password
        </div>
        <div style={{ color: "#666", marginTop: "8px", fontSize: "14px" }}>
          Enter your new password
        </div>
      </div>

      <div className="p-field" style={{ marginBottom: "20px" }}>
        <label style={{ display: "block", marginBottom: "6px", fontWeight: "600" }}>
          New Password
        </label>
        <Password
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Enter new password"
          style={{ width: "100%" }}
          inputStyle={{ height: "45px", width: "100%" }}
          feedback={true}
          toggleMask
        />
      </div>

      <div className="p-field" style={{ marginBottom: "20px" }}>
        <label style={{ display: "block", marginBottom: "6px", fontWeight: "600" }}>
          Confirm Password
        </label>
        <Password
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm new password"
          style={{ width: "100%" }}
          inputStyle={{ height: "45px", width: "100%" }}
          feedback={false}
          toggleMask
        />
      </div>

      <Button
        label={loading ? "Resetting..." : "Reset Password"}
        onClick={handleResetPassword}
        disabled={loading}
        style={{ background: "#2A2B8F", height: "45px", width: "100%" }}
      />

      <div style={{ textAlign: "center", marginTop: "12px" }}>
        <span
          style={{ color: "#2A2B8F", cursor: "pointer", fontSize: "14px" }}
          onClick={() => setStep(2)}
        >
          Back to OTP Verification
        </span>
      </div>
    </>
  );

  return (
    <div style={{ height: "100%", display: "flex" }}>
      <Toast ref={toast} />
      <div
        style={{
          background: "white",
          width: "50%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div style={{ width: "100%", maxWidth: "400px", padding: "0 20px" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
            <img src={Logo} alt="FKCCI Logo" style={{ maxWidth: "200px" }} />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "8px",
              marginBottom: "24px",
            }}
          >
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                style={{
                  width: step >= s ? "40px" : "30px",
                  height: "4px",
                  borderRadius: "2px",
                  background: step >= s ? "#2A2B8F" : "#ddd",
                  transition: "all 0.3s",
                }}
              />
            ))}
          </div>

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}

          <div style={{ textAlign: "center", marginTop: "24px" }}>
            <span
              style={{ color: "#2A2B8F", cursor: "pointer", fontSize: "14px", fontWeight: "600" }}
              onClick={() => navigate("/")}
            >
              Back to Login
            </span>
          </div>
        </div>
      </div>
      <div style={{ width: "50%", height: "100vh" }}>
        <img src={LogoLeft} alt="" style={{ width: "100%", height: "100%" }} />
      </div>
    </div>
  );
};

export default ForgotPassword;
